/**
 * **The attribute treatment's geometry and motion, in one place** (LF-6186,
 * promoted to production by LF-6304 / [ATTR-3]).
 *
 * Every measurement this surface depends on, named. It exists because the same
 * class strings were written out ten times across the grid — `text-13px` ten
 * times, `px-1.5 py-1` seven, the leading nine — and a second surface adopting
 * this treatment would have copied the strings and drifted from them one fix at
 * a time. That is the failure mode the root `CLAUDE.md` calls *"never restate a
 * value the codebase already owns"*.
 *
 * **These are the rules, in executable form.** A new surface that wants this
 * treatment imports them; it does not retype them. If a value here turns out to
 * be wrong, one edit fixes every screen using it.
 *
 * Each constant records *why* it is what it is, because several look arbitrary
 * and are not — most were arrived at by measuring a render, and a few by
 * measuring a render twice after the first answer was wrong.
 *
 * ## Where it lives
 *
 * `components/attributes/`, beside `attribute-grid.tsx` and its two halves —
 * the shared grid this treatment is expressed through. It sat under
 * `components/prototypes/form-system/` until [ATTR-3] and under
 * `components/leads/` until **LF-6388 / [ATTR-10]**, which is the relocation
 * this note used to anticipate. It was deliberately last in the epic, because
 * moving a component while it is still being widened buries a mechanical change
 * inside a behavioural one.
 *
 * ## Two values were corrected on the way in, and both mattered
 *
 * The prototype copy had drifted from the grid it was extracted from. The
 * promotion's contract is that **production renders exactly as it did before**
 * — the enquiry drawer and the three grid consumers are unchanged — so where
 * the two disagreed, the shipped value won:
 *
 * · **{@link ATTRIBUTE_LABEL} carried `w-33 max-w-[40%]`, a fixed 112px column
 *   that never yielded.** Its own doc comment described that as a defect and
 *   named LF-6240 as the ticket tracking it — and LF-6240 then *shipped the
 *   fix* into `attribute-grid.tsx` without the prototype token following.
 *   Promoting the stale string and wiring the grid to it would have reverted
 *   that fix. The column is now {@link ATTRIBUTE_LABEL_WIDTH}, which came
 *   across from the grid along with its measurements.
 *
 * · **{@link ATTRIBUTE_LEADING} was `leading-[19.5px]`, and that value is
 *   right — but adopting it is a visual change, so the grid's `leading-5`
 *   stays.** The prototype's premise was checked against the rendered enquiry
 *   drawer rather than reasoned about, and it holds: the label computes 17px
 *   and the value 19.5px, so their first lines really do sit 1.25px apart.
 *   Promoting 19.5px would fix that — and would also add 2.5px to every row of
 *   four shipped surfaces, which this ticket's contract forbids. The token
 *   therefore carries the **shipped** value and the gap is recorded instead;
 *   the full measurement is under {@link ATTRIBUTE_LEADING}.
 */

import type { CSSProperties } from 'react';

/** Local join — this module deliberately imports no runtime helpers. */
const cnJoin = (...parts: string[]) => parts.join(' ');
import type { RowMotionSpec } from './use-row-motion';

// =============================================================================
// Type
// =============================================================================

/**
 * **The line-height every attribute row shares**, label, read-out and every
 * editor alike, so their first lines sit on one line box
 * (`docs/attribute-treatment.md` §1.6).
 *
 * `19.5px`, stated in pixels: two different font sizes line up only if they
 * share a line-height in *pixels*, and 19.5px is the larger of the two natural
 * leadings (`text-13px` at Tailwind's 1.5 ratio), so nothing is squeezed.
 *
 * ## Written as an arbitrary property, not `leading-[19.5px]`, and this is
 * ## the whole point (VALE-1505)
 *
 * `lib/utils.ts` registers the `text-<N>px` scale into tailwind-merge's
 * `font-size` group, and that group **conflicts with `leading-*`** (a stock
 * `text-sm` sets both). So in `cn(…, 'leading-5', ATTRIBUTE_VALUE_TEXT)` the
 * leading was silently discarded, and in the reverse order it survived. The
 * token shipped as `leading-5` (17px) for a year on exactly that footing: the
 * read-out happened to drop it and render at 19.5px, the single-line `Input`
 * was hand-ordered to drop it too, and **every other editor kept it**, so a
 * `select`, `money`, `date`, `multiline` or popover row lost 2.5px of height
 * the instant it was clicked, and the label sat 1.25px above its own value on
 * every row of every surface. Measured on the rendered app at VALE-1505:
 * label 23.78px (17px leading) beside a value 26.28px (19.5px); open popover
 * trigger 23.78px against the 26.28px read-out it replaced.
 *
 * An arbitrary property lives in its own tailwind-merge group, so it can never
 * be dropped by a font-size token whatever order a call site composes them in.
 * That removes the trap rather than documenting it: no caller has to remember
 * to put the size before the leading.
 */
export const ATTRIBUTE_LEADING = '[line-height:19.5px]';

/** The value's own type. Paired with {@link ATTRIBUTE_LEADING} everywhere. */
export const ATTRIBUTE_VALUE_TEXT = 'text-13px';

/** The label's type — a step down from the value it names. */
export const ATTRIBUTE_LABEL_TEXT = 'text-12px';

/** A value the reader came for. */
export const ATTRIBUTE_VALUE_INK = 'text-foreground';

/**
 * A label, and the `Set <label>…` placeholder that stands in for an empty
 * value. The same ink deliberately: both are chrome around the record rather
 * than content of it.
 */
export const ATTRIBUTE_MUTED_INK = 'text-muted-foreground';

/**
 * The same ink, applied only while a control is showing its **placeholder** —
 * for a live `Select` that has no read-out to fall back on (LF-6314).
 *
 * The grid's own select editor needs nothing like this: it opens *over* a
 * read-out, and the read-out is what renders `Set <label>…` in
 * {@link ATTRIBUTE_MUTED_INK}, so its `SelectValue` carries no placeholder at all
 * (`attribute-field-control.tsx`). A surface whose controls are permanently live — the
 * inventory drawer's compact fields — has no read-out to borrow that from, and
 * `SelectTrigger`'s default `data-placeholder:text-muted-foreground` is a
 * *darker* neutral (`oklch(0.543 0 0)` since VALE-523, against slate-400's ≈0.70), so an unset
 * value there reads as content rather than as chrome.
 *
 * **The colour is written out literally rather than interpolated from
 * {@link ATTRIBUTE_MUTED_INK}, for the same reason {@link ATTRIBUTE_LABEL}'s
 * width is:** Tailwind scans source text for whole utilities, and
 * `` `data-placeholder:${ATTRIBUTE_MUTED_INK}` `` is not one — the class would
 * simply never be emitted. The two sit three lines apart, which is as close to
 * one source of truth as the scanner permits.
 */
export const ATTRIBUTE_MUTED_INK_PLACEHOLDER =
  'data-placeholder:text-muted-foreground';

/**
 * A group heading — the small uppercase title that names a section of the
 * record (`docs/attribute-treatment.md` §1.10, the 7a "Groups: rule" format).
 *
 * **The type treatment only. The divider is deliberately NOT in here**, because
 * group chrome is *surface-dependent* and the divider is the part that varies:
 * a white surface (the Contacts drawer) rules the title off with a hairline
 * beneath it, while a tinted one (the enquiry drawer) already steps its groups
 * forward with `LeadDrawerCard` and rules *above* the heading instead. Folding a
 * divider in here would make one of the two consumers wrong. What both surfaces
 * genuinely share is the type, so that is what this names — and the white
 * surface's divider now has an owner of its own in `AttributeSections`
 * (`./attribute-section.tsx`).
 *
 * ## `tracking-wide`, corrected from `tracking-wider` (LF-6350 / [ATTR-8])
 *
 * This token shipped carrying `tracking-wider`, and that is **drift rather than a
 * decision**. LF-6350 asked which of the two is right on the premise that the
 * token is shared with the enquiry drawer, so changing it could not be
 * Contacts-local — and that premise was false, which is what settles it:
 *
 * · The prototype this treatment is ported from types the heading
 *   `tracking-wide` (`attribute-contact-form.tsx`, whose header says it is
 *   lifted verbatim from `LeadDrawerCard`).
 * · **`LeadDrawerCard` — the enquiry drawer's own group chrome — renders
 *   `tracking-wide` too**, and was not consuming this token at all. So the token
 *   had exactly one consumer (the Contacts drawer) and disagreed with *both* the
 *   prototype and the shipped surface it was extracted from.
 *
 * Measured on the two rendered drawers at 672px: story 7a's heading resolves to
 * `letter-spacing: 0.3px`, the shipped Contacts drawer's to `0.6px`. Correcting
 * the token therefore moves only Contacts, and moves it *onto* the enquiry
 * drawer's value rather than away from it. `LeadDrawerCard` now binds here,
 * which is what makes this one fact instead of two strings that happen to agree.
 *
 * (`lead-summary-tab.tsx` still hand-types a `tracking-wider` heading. That is a
 * different surface with its own spacing, so binding it here would be a visual
 * change outside this ticket — recorded as a known restatement rather than swept
 * in quietly.)
 */
export const ATTRIBUTE_GROUP_TITLE = `${ATTRIBUTE_LABEL_TEXT} font-semibold tracking-wide text-muted-foreground uppercase`;

/**
 * **The space between a section's rule and the first block under it** — the one
 * vertical gap the group chrome owns (VALE-1689).
 *
 * A token rather than the `pt-2` `AttributeSections` used to hand-type, for the
 * reason {@link ATTRIBUTE_GROUP_TITLE}'s own docblock gives about the title's
 * type: a spacing string typed into a shared component is a private decision
 * with no owner, and it drifts. `pb-2` above the rule and this below it are one
 * pair, so they are read from one place.
 *
 * **It applies to a section's FIRST rendered block and to nothing else, whatever
 * kind of block that is.** Every later block in the same section adds nothing,
 * so the space between any two things inside a section is
 * {@link ATTRIBUTE_ROW}'s own `py-0.5` and never depends on where a block
 * boundary happens to fall.
 *
 * A block exists for layout reasons — a differing column count, a logical
 * cluster in a spec — and none of those reasons are visible on screen, so a gap
 * keyed to them puts an unexplained break partway down what the reader reads as
 * one flat list. Before VALE-1689 every block wrapper carried this, which made
 * a block boundary 10.2px against its neighbours' 3.4px, marking nothing.
 *
 * A **titled section** break is unaffected: that space is the section stack's
 * `gap-6` plus the heading's own rule, and it marks something the reader sees.
 */
export const ATTRIBUTE_SECTION_BODY_LEAD = 'pt-2';

/**
 * **The hairline under a section's title, and the space above it** (VALE-1689).
 *
 * The white-surface divider {@link ATTRIBUTE_GROUP_TITLE}'s docblock says is
 * deliberately *not* folded into the type token, given an owner of its own here
 * rather than left as a class string typed into `AttributeSections`. It is the
 * other half of the pair {@link ATTRIBUTE_SECTION_BODY_LEAD} completes: `pb-2`
 * separates the title from the rule, the lead separates the rule from the first
 * block.
 *
 * Still white-surface only — a tinted surface rules *above* its heading
 * (`LeadDrawerCard`), which is why this is a section-chrome token and not part
 * of the title's type.
 */
export const ATTRIBUTE_SECTION_TITLE_RULE =
  'border-border-subtle border-b pb-2';

/**
 * **A calmer selection colour, for a surface that gets selected constantly**
 * (LF-6186 round 4, promoted to production by LF-6350 / [ATTR-8]).
 *
 * The browser default is a saturated system blue which, on a surface this quiet,
 * reads closer to an error state than to a selection — and an inline-commit form
 * gets selected far more than an ordinary one, because **the editor select-alls
 * on open**, so every single correction flashes it. `blue-100` keeps the meaning
 * and drops the shout.
 *
 * A Tailwind `selection:` variant rather than a `::selection` rule, since the
 * frontend rules forbid custom CSS. Applied by `AttributeSections`, so a surface
 * inherits it along with the group chrome instead of having to remember it — it
 * was present in both prototypes and in neither production surface, which is
 * exactly what happens to a decision with no owner.
 */
export const ATTRIBUTE_SELECTION =
  'selection:bg-info-subtle selection:text-foreground';

// =============================================================================
// The row
// =============================================================================

/**
 * The vertical inset shared by a label and the value beside it.
 *
 * Load-bearing rather than decorative: with a shared {@link ATTRIBUTE_LEADING}
 * this is the other half of what puts the two first lines on one line — the
 * label is a plain span and the value is a padded button, so unequal padding
 * offsets them however well the leading matches.
 */
export const ATTRIBUTE_INSET_Y = 'py-1';

/**
 * The horizontal inset shared by a read-out and the editor that replaces it.
 *
 * Load-bearing rather than decorative: the editor opens *over* the value, so
 * any difference here shifts the text sideways on click. `Input`'s own `px-3`
 * moved it 6px.
 */
export const ATTRIBUTE_INSET = `px-1.5 ${ATTRIBUTE_INSET_Y}`;

/** The radius shared by the hover target, the read-out and the open editor. */
export const ATTRIBUTE_RADIUS = 'rounded-md';

/**
 * One attribute row: a label column and a value cell on a shared baseline.
 *
 * `items-start`, not `items-baseline`: the label truncates, and a flex item
 * with `overflow: hidden` has its baseline synthesised from its bottom margin
 * edge, which would drop every label below its own value. Both sides carry the
 * same {@link ATTRIBUTE_LEADING} and {@link ATTRIBUTE_INSET_Y} instead, which
 * lines the first lines up without relying on baseline alignment at all.
 */
export const ATTRIBUTE_ROW = 'flex min-w-0 items-start gap-4 py-0.5';

/**
 * The same row, with its **vertical** rhythm closed up (VALE-1494).
 *
 * Distinct from {@link ATTRIBUTE_ROW_COMPACT}, which is a whole read-only
 * density: that one also tightens the label/value gap to `gap-2`, and its
 * docblock explains why it is gated on every row being `readOnly`. This is the
 * vertical half alone, and it carries none of that risk — a row's HIT TARGET is
 * {@link ATTRIBUTE_INSET_Y} on the value itself, which is untouched here, so
 * closing the row's own `py-0.5` takes 4px of pure air out from between two
 * lines and shrinks nothing anybody clicks.
 *
 * For a grid that is one item's detail inside a list, where the gaps between
 * its rows compete with the gaps between the list's items.
 */
export const ATTRIBUTE_ROW_TIGHT = 'flex min-w-0 items-start gap-4 py-0';

/**
 * The row, at **read-only summary density** (VALE-977) — `py-0` instead of
 * `py-0.5`.
 *
 * A separate token rather than a smaller value on {@link ATTRIBUTE_ROW},
 * because that name carries the LF-6240 measurements and a surface wanting
 * something tighter must not redefine what those measurements mean.
 *
 * The label/value gap tightens with it (`gap-2`), and the grid's column gutter
 * widens to compensate — a label and its value are one thing and should read
 * as one, while the separation that matters is between the pairs. On a summary
 * grid the two were near enough equal that the pairs stopped grouping.
 *
 * Only for a grid whose every row is `readOnly`. The default padding is sized
 * for a row you can click into and type in — the value's hit box and the
 * editor's own inset both live inside it — so tightening a live grid would
 * shrink a target, not just a gap. A block that only reads out has no target to
 * shrink, and there the padding is pure air between lines a reviewer is
 * scanning. Reach for it through `AttributeGrid`'s `density="compact"`, which
 * is where the readOnly-only rule is enforced.
 */
export const ATTRIBUTE_ROW_COMPACT = 'flex min-w-0 items-start gap-2 py-0';

/**
 * The slot a value occupies — everything the label column leaves.
 *
 * `flex-1` because this is the **editor's** slot: an open `Input` sized to its
 * own intrinsic width would be a fraction of the column it replaced, so the
 * control takes the whole of what the label leaves. `min-w-0` because the row is
 * a grid item and an email address is one unbreakable word, which would
 * otherwise hold the row wider than its card and defeat every `truncate` inside
 * it.
 *
 * **The value at rest is the same width** (VALE-349) — see
 * {@link ATTRIBUTE_VALUE_TARGET}. The two were deliberately different widths for
 * one release and are deliberately the same again: what you click on and what
 * you type into are both the value column.
 */
export const ATTRIBUTE_VALUE_SLOT = 'flex min-w-0 flex-1';

/**
 * **The value at rest — the target is the value column, on every row**
 * (VALE-349, reversing the VALE-130 follow-up).
 *
 * A hugging target is only as wide as its own text, so the size of the thing a
 * reader clicks changed row by row with the length of the value or its
 * placeholder: in the Contacts drawer `Miller` and `Set date of death…` present
 * targets of very different widths in the same column, one under the other. A
 * floor ({@link ATTRIBUTE_TARGET_MIN_W}) answered *"is it big enough to hit"*
 * and nothing at all about *"is it the same thing twice"* — and on a record
 * surface, a column of ragged hit areas is the more visible defect of the two.
 *
 * So the target is the column again, and the two costs the hug was introduced
 * to remove are paid for directly rather than by shrinking every row:
 *
 * · **A record reading as a field of edit triggers** was the honest half of the
 *   complaint, and it is a *tint* problem, not a *width* problem — the hover
 *   fill ({@link ATTRIBUTE_HOVER}) is one step weaker than the surface's own
 *   `slate-100` for exactly this reason. A row still lights only under the
 *   pointer; it is simply a row-shaped light rather than a word-shaped one.
 * · **A `fullWidth` row's target being the whole card** is a real defect and
 *   stays fixed — see {@link ATTRIBUTE_TARGET_CAPPED}, which caps a spanning
 *   row at the width a non-spanning row's value cell gets, so *"every row's
 *   target is the same width"* is true of the spanning rows too rather than
 *   true only of the rest.
 *
 * `min-w-0` because the row is a grid item and an email address is one
 * unbreakable word, which would otherwise hold the row wider than its card and
 * defeat every `truncate` inside it. It is the same pair as
 * {@link ATTRIBUTE_VALUE_SLOT}, and deliberately so.
 */
export const ATTRIBUTE_VALUE_TARGET = 'min-w-0 flex-1';

/**
 * **The floor for a target that is NOT in a value column** — chrome, where
 * there is no column to fill.
 *
 * {@link AttributeChromeValue} renders one attribute beside a drawer title, so
 * the surrounding box is the header rather than a grid row: filling it would
 * stretch a `Status` pill across the whole header and read as an unstyled
 * input. It hugs instead, and then needs the floor a hugging target always
 * needs — `No` is two characters, roughly 24px of tint, which reads as a
 * decoration rather than as something to click. 24 spacing units is **81.6px**
 * at this theme's 3.4px `--spacing`, comfortably past the 44px pointer target
 * WCAG 2.5.8 asks for.
 *
 * The explicit `min-width` is also what lets the box keep shrinking:
 * `min-width: auto` on a flex item refuses to go below its content's
 * min-content width, so an unbreakable value would hold it wider than its
 * header.
 */
export const ATTRIBUTE_TARGET_MIN_W = 'min-w-24';

/** {@link ATTRIBUTE_TARGET_MIN_W} plus the ceiling — the whole chrome target. */
export const ATTRIBUTE_CHROME_TARGET = `${ATTRIBUTE_TARGET_MIN_W} max-w-full`;

/**
 * **The cap a `fullWidth` row's resting target wears** (VALE-349).
 *
 * A `fullWidth` row spans its grid's columns so the *value* has room to wrap —
 * the matter Overview's estate card gives its residential address all three
 * columns. Letting the resting target span with it put ~350px of address inside
 * a ~1100px tint: three quarters of it empty space lighting up as if it were the
 * value, which is the one complaint the VALE-130 follow-up made that survives
 * {@link ATTRIBUTE_VALUE_TARGET} going back to the column.
 *
 * **The cap is `fit-content`, not a fixed width** (VALE-863). It was one
 * column's value width — published as `--attr-target-max-w`, now removed — on
 * the reasoning
 * that every row's resting target should be the same width — but that fixed a
 * width when what the complaint was about is *empty space inside the tint*, and
 * those are different variables. A fixed cap gets the short case right and the
 * long case wrong in the same stroke: a residential address longer than one
 * column wrapped to three lines inside half the row while the other half sat
 * empty, which is what Tom's 27 Aug review called *"squished"* and asked to be
 * given the whole row.
 *
 * `fit-content` resolves to `min(max-content, available)`, so it answers the
 * actual question — **the tint is exactly as wide as the value**. A short
 * address hugs its text, which is *tighter* than the old cap rather than
 * looser; a long one uses the span it was already given, and stops wrapping.
 * The VALE-349 complaint the cap exists for is served better by this than by
 * the number it replaces, which is why this is a correction rather than a
 * reversal.
 *
 * It applies **at rest only** — an open editor takes the span it was given,
 * because that is what the span was for.
 *
 * **A `multiline` row is exempt entirely** (VALE-493) — see the call site in
 * {@link AttributeGrid}. On a row whose editor is an inline box that grows as it
 * is typed into, "capped at rest, released on open" is a width change at the
 * exact moment the reader starts working, and it re-wrapped `Matter notes` from
 * 7 lines to about 3 while moving every row below it. Those rows are measured
 * against their span in both states instead. The address keeps the cap: it is
 * `fullWidth` without `multiline`, and a popover editor never inherits the
 * resting width, so there is no jump to remove and a short address in a
 * card-wide tint is the defect the cap was written for.
 */
export const ATTRIBUTE_TARGET_CAPPED = 'max-w-fit';

/**
 * **One grid column's worth of width, for a kept component that is really a
 * single control** (VALE-863).
 *
 * A `component` block renders `w-full` because most of them own a whole layout —
 * a price comparison, an instruction editor. But some are one input wearing a
 * component's clothes, and the share registry picker is the case Tom's 27 Aug
 * review named: *"the Computershare field does not need the full page width."*
 * Stretched edge to edge it read as the most important thing in the drawer,
 * beside rows whose values stop at their column.
 *
 * **Expressed against the grid, not as a measured pixel count.** The two-column
 * grid separates its columns by `gap-x-8`, so one column is half the content
 * width less half that gap, `calc(50% - 4 * var(--spacing))`. It shipped as
 * `calc(50% - 1rem)` on the assumption that `gap-x-8` is 2rem; under this
 * theme's `--spacing` of 0.2125rem it is 1.7rem, so the cap was 3.4px too
 * narrow (VALE-1505). Written in `--spacing` steps it rides the density knob
 * exactly as the gap does. Write it as `max-w-[336px]` and it is right until
 * someone changes the gap or the drawer width; written this way it simply
 * stays one column.
 */
export const ATTRIBUTE_COMPONENT_ONE_COLUMN =
  'max-w-[calc(50%-4*var(--spacing))]';

/**
 * **The value half of a row — the cell, and the refusal that belongs to it**
 * (VALE-349).
 *
 * A column rather than the cell alone, because a refused commit now renders
 * under the value it was refused for instead of as one line above the whole
 * grid. `items-stretch` (the default) is what keeps the cell the full width of
 * the column, so the hit area is the column even though the message under it is
 * only as wide as its text.
 *
 * `gap-0.5` — the message is attached to the value, not a sibling of it, and a
 * row's own vertical rhythm ({@link ATTRIBUTE_ROW}'s `py-0.5`) already separates
 * it from the row below.
 */
export const ATTRIBUTE_VALUE_COLUMN = 'flex min-w-0 flex-1 flex-col gap-0.5';

// =============================================================================
// The label column
// =============================================================================

/**
 * **The custom property the label column's width is published through.**
 *
 * It is resolved against the grid — see {@link ATTRIBUTE_LABEL_WIDTH} — so
 * every row lands on the identical number, including a `fullWidth` row that
 * spans both columns and would otherwise compute its own.
 *
 * **The name is written out literally in {@link ATTRIBUTE_LABEL} rather than
 * interpolated from this constant, and that is not an oversight.** Tailwind
 * scans source text for whole utilities; `w-[var(${ATTRIBUTE_LABEL_WIDTH_VAR})]`
 * is not one, so the class would simply never be emitted. The two literals are
 * ten lines apart in this one file, which is as close to a single source of
 * truth as the scanner allows.
 */
export const ATTRIBUTE_LABEL_WIDTH_VAR = '--attr-label-w';

/**
 * **The label column's width — one value shared by every row in the grid**
 * (LF-6240, `docs/attribute-treatment.md` §1.1).
 *
 * Three behaviours are required, and the `w-33 max-w-[40%] shrink-0` this
 * replaced delivered none of them:
 *
 * 1. With slack available the column takes the width it needs **up to a
 *    ceiling**, so a long label is not clipped on a wide drawer.
 * 2. Under pressure both sides truncate, the label giving up width **before and
 *    faster than** the value.
 * 3. The value is **never** the first thing to clip.
 *
 * **Why a container query rather than flex-shrink.** Letting the label shrink
 * against its own row's value (`flex: 0 3 auto`) satisfies 2 and 3, but sizes
 * every label against *that row's* value text — so `Relationship to deceased`
 * and `Phone` end up 55px apart and the values stop sharing a left edge, which
 * is the one thing the shared column exists to prevent. `cqw` is read from the
 * **grid**, so all rows resolve to the identical width.
 *
 * **The slope is steeper than proportional, which is what "faster" means.** A
 * plain percentage yields the label and the value the same *share* of each lost
 * pixel, so the value still loses more absolute width. The `- 45px` intercept
 * makes the label's share of the row fall as the row narrows (≈34% at a 444px
 * row, ≈29% at 288px) — Attio's own priority, and Damien's steer on LF-6186:
 * with limited real estate, show the reader the values rather than the labels.
 *
 * Measured against the numbers on LF-6240, at the 1920 viewport the defect was
 * reported at (drawer 1320px → grid 915px → 444px row):
 *
 * | Row width | Label was | Label now | Longest label needs |
 * | --------- | --------- | --------- | ------------------- |
 * | 444px     | 112.2px   | **153px** (ceiling) | 152px — clears |
 * | 350px     | 112.2px   | 112.4px   | unchanged            |
 * | 288px     | 112.2px   | 84.6px    | yields, value +27.6px |
 *
 * The ceiling is 153px because the longest label in the product
 * (`Relationship to deceased`) needs 152px; 40px of widening clears every one
 * of the six that clipped, and the value cell had 279px spare at the median to
 * pay for it. Ceiling and floor are written in `--spacing` steps (45 and 21)
 * rather than raw px so they keep riding the density knob, exactly as `w-33`
 * did.
 *
 * **Where the two-column coefficients come from.** A row is half the grid less
 * half of `gap-x-8`, so substituting `row = (100cqw - 8 * --spacing) / 2` into
 * `0.45 * row - 45px` gives both of them:
 *
 * - `22.5cqw`   = `0.45 * 100cqw / 2`
 * - `1.8 * --spacing` = `0.45 * (8 * --spacing / 2)` — the label's 45% **share
 *   of the half-gap**, i.e. 6.12px. It is *not* the half-gap itself (13.6px):
 *   both terms carry the 0.45, so anyone retuning that percentage has to scale
 *   this one with it.
 *
 * Below `sm` a `columns={2}` grid is laid out as one column, so it uses the
 * one-column slope and the floor does most of the work — the label goes to
 * 72px on a phone and the value takes the rest. That is the steer, not a
 * regression: the narrower the surface, the more the values are what matters.
 *
 * ## The `3` rung (VALE-129)
 *
 * Same derivation, extended rather than re-guessed: a three-column row is
 * `(100cqw - 2 * gap-x-8) / 3` — two internal gaps rather than one — so
 * substituting into `0.45 * row - 45px` gives `15cqw - 2.4 * --spacing - 45px`:
 * `15cqw` is `0.45 * 100cqw / 3`, and `2.4 * --spacing` is `0.45 * (2 * 8 *
 * --spacing / 3)`, the label's 45% share of both gaps. The estate record is 8
 * rows on a full-width surface; at two columns each value cell gets ~480px for
 * a single name, which reads as a band of air — three columns lands each cell
 * near 320px.
 *
 * **It carries the share but not the `- 45px` intercept, and that is the one
 * place the three rungs deliberately differ** (VALE-130, measured on the first
 * surface to render this rung). At 1 and 2 columns a narrow row means a narrow
 * *surface* — a phone, a 320px rail — where the steer above is right: show the
 * values. At 3 columns a narrow row means a **wide surface cut into thirds**,
 * and the reader is scanning across three label/value pairs on one line with no
 * vertical run of the same field to disambiguate a clipped label from its
 * neighbours. Carrying the intercept into this rung applies a cramped-surface
 * penalty to a surface that is not cramped, and it measured as exactly that:
 *
 * | Grid width | Label was | Label now | Longest label needs |
 * | ---------- | --------- | --------- | ------------------- |
 * | 1196px (1920 viewport) | 126px | **153px** (ceiling) | 147px — clears |
 * | 1088px (1440 viewport) | 110px | **153px** (ceiling) | 147px — clears |
 * | 928px (1280 viewport)  | 86px  | 131px     | 147px — still yields |
 *
 * The 1280 row stays honest rather than being tuned away: a 291px row cannot
 * hold a 147px label *and* a 197px value, so something ellipsises there at any
 * setting, and the original steer decides which. `Assisting team members` and
 * `Death certificate number` (147px / 146px) are the two longest labels on the
 * estate record; the ceiling is unchanged at 153px, which is still the longest
 * label in the product (`Relationship to deceased`, 152px).
 */
/**
 * The compact label column: a constant, not a clamp.
 *
 * The responsive slope exists so a record grid's label column can grow with the
 * surface — right when the labels are arbitrary and the values long. A summary
 * block's labels are a known, short, fixed set, so the slope only ever did one
 * thing: on a narrower container it resolved below what they need and clipped
 * them to `Date of …` and `Bank ac…`. 96px fits the longest of them
 * (`Bank account`) and never moves.
 */
const LABEL_WIDTH_COMPACT = 'calc(24 * var(--spacing))';

/**
 * The label column at **read-only summary density** (VALE-977) — the same slope
 * as {@link ATTRIBUTE_LABEL_WIDTH}, clamped between a lower floor and a much
 * lower ceiling.
 *
 * A separate rung rather than smaller numbers on the default, for the reason
 * {@link ATTRIBUTE_ROW_COMPACT} exists: those bounds carry the LF-6240
 * measurements and a surface wanting something tighter must not redefine what
 * they mean.
 *
 * **The ceiling is what actually bites.** On a wide surface the slope resolves
 * past the default 45-step ceiling, so a two-word label sat in a 180px column
 * with its value starting an inch away. That is correct for a record a reader
 * scans down — the shared column is what lets the values line up — but a
 * summary block's labels are short and its values are the point, so 24 steps
 * (96px) gives the rest to the value. The ceiling is what this rung is really
 * for: on a wide surface the slope resolves past it every time, so the ceiling
 * IS the width.
 *
 * 96px rather than tighter because it is the floor for the longest label the
 * treatment's own consumers use — 88px clipped `Bank account`, and a truncated
 * label costs more than the space it saves.
 *
 * The floor drops with it, for a narrow block where the slope is already below
 * the default floor.
 */
export const ATTRIBUTE_LABEL_WIDTH_COMPACT = {
  1: LABEL_WIDTH_COMPACT,
  2: LABEL_WIDTH_COMPACT,
  3: LABEL_WIDTH_COMPACT
} as const;

export const ATTRIBUTE_LABEL_WIDTH = {
  /** Row = the whole grid. */
  1: 'clamp(calc(21 * var(--spacing)), calc(45cqw - 45px), calc(45 * var(--spacing)))',
  /** Row = half the grid, less half of `gap-x-8`. */
  2: 'clamp(calc(21 * var(--spacing)), calc(22.5cqw - 1.8 * var(--spacing) - 45px), calc(45 * var(--spacing)))',
  /**
   * Row = a third of the grid, less two-thirds of `gap-x-8`. No `- 45px`
   * intercept — see the `3` rung's own section above for why this rung alone
   * drops it.
   */
  3: 'clamp(calc(21 * var(--spacing)), calc(15cqw - 2.4 * var(--spacing)), calc(45 * var(--spacing)))'
} as const;

/**
 * The inline style a grid puts on its `@container` element to publish
 * {@link ATTRIBUTE_LABEL_WIDTH} to every row inside it.
 *
 * A function rather than a lookup table so no caller has to know the custom
 * property's name — and so `@container` and the variable can never end up on
 * two different elements, which would resolve the `cqw` against the wrong box.
 *
 * It published a second property, `--attr-target-max-w`, until VALE-863 made
 * {@link ATTRIBUTE_TARGET_CAPPED} `fit-content`; nothing read it after that, so
 * it went rather than staying as a value a later reader would bind to.
 */
export function attributeLabelWidthStyle(
  columns: 1 | 2 | 3,
  density: 'default' | 'compact' = 'default'
): CSSProperties {
  return {
    [ATTRIBUTE_LABEL_WIDTH_VAR]:
      density === 'compact'
        ? ATTRIBUTE_LABEL_WIDTH_COMPACT[columns]
        : ATTRIBUTE_LABEL_WIDTH[columns]
  } as CSSProperties;
}

/**
 * The label column itself — a shared width that ellipsises, rather than a label
 * sized to its own text.
 *
 * This is the half of the treatment that makes left-aligned values work:
 * `Relationship to deceased` and `Phone` otherwise start their values 100px
 * apart, and the value column stops being a column.
 *
 * `shrink-0` is load-bearing and stays: `--attr-label-w` is *already* the
 * yielded width (it is a function of the grid — see
 * {@link ATTRIBUTE_LABEL_WIDTH}), so leaving flex-shrink on would let the label
 * lose width a **second** time, weighted against this row's own value text,
 * which is exactly what breaks the shared column.
 *
 * Compose with {@link ATTRIBUTE_LEADING}; the two are separate so a caller can
 * see that the label and the value share one leading rather than each carrying
 * their own.
 */
export const ATTRIBUTE_LABEL = `${ATTRIBUTE_LABEL_TEXT} w-[var(--attr-label-w)] shrink-0 truncate ${ATTRIBUTE_INSET_Y} ${ATTRIBUTE_MUTED_INK}`;

/**
 * The label at **read-only summary density** — sized to its own text, not to a
 * shared column (VALE-977).
 *
 * The shared column is the right default and its reason is real: it is what
 * makes every value in a column start at the same x, so the values read as a
 * column instead of a ragged edge. But the width it reserves is the width of
 * the LONGEST label, so the space after a short one is that difference — and on
 * a summary grid, where `Phone` sits opposite `Date of birth`, the two pairs
 * visibly disagree about how far a value sits from its label.
 *
 * A summary block has no editors to line up and only a handful of short labels,
 * so it trades the shared column for an even gap: every label is followed by
 * exactly one `gap-2`, and the few pixels of raggedness that buys are worth
 * less than the inconsistency they replace.
 */
export const ATTRIBUTE_LABEL_COMPACT = `${ATTRIBUTE_LABEL_TEXT} shrink-0 ${ATTRIBUTE_INSET_Y} ${ATTRIBUTE_MUTED_INK}`;

// =============================================================================
// The hover target
// =============================================================================

/**
 * The resting hover tint.
 *
 * `slate-50`, deliberately one step weaker than the `slate-100` this surface
 * uses elsewhere: a `select` row renders its value as a subtle `StatusBadge`,
 * which is literally `bg-slate-100`, so highlighting the cell in it makes the
 * pill vanish on the one interaction meant to reveal the row. Verified by
 * screenshot, not by comparing hex values.
 */
export const ATTRIBUTE_HOVER = 'hover:bg-surface-subtle';

/**
 * The same tint, driven by the row's `group/copy` rather than by the value
 * button's own hover.
 *
 * On a copyable or clearable row the affordances are **siblings** of the value
 * button, so moving the pointer onto one leaves the button and would drop the
 * highlight at the exact moment the row is most active. The group covers the
 * pair. Rows with no affordance never open that group, so this simply never
 * matches there.
 *
 * A separate constant rather than a variant composed onto
 * {@link ATTRIBUTE_HOVER} for the same reason the label width is written out
 * literally: Tailwind scans for whole utilities, and an interpolated variant is
 * not one.
 */
export const ATTRIBUTE_HOVER_IN_COPY_GROUP =
  'group-hover/copy:bg-surface-subtle';

/**
 * The value cell as the prototype's *refined* treatment draws it — the hover
 * target and the owner of the row's affordances in one box, so the tint and the
 * open-editor chrome enclose the value and its copy / clear chips together.
 *
 * **The default since LF-6388 / [ATTR-10]** — the Contacts drawer, the
 * Enquiries drawer, `/enquiries/{id}`, the cost-agreement rail card, the
 * inventory drawer and the matter Overview's estate card all render through it.
 * One surface still opts out via {@link AttributeGrid}'s `legacyChrome`, where
 * this is not mounted at all and the read-out keeps its own tint and
 * affordances.
 *
 * **It carries no width of its own** (VALE-349). In a grid row it takes
 * {@link ATTRIBUTE_VALUE_TARGET} — the value column, at rest and open alike —
 * and in a drawer header {@link AttributeChromeValue} gives it
 * {@link ATTRIBUTE_CHROME_TARGET} instead, because a header has no column to
 * fill. The two used to be one string that hugged, which is what made a header
 * `Status` and a grid row the same shape for the wrong reason.
 *
 * `pr-1` so a revealed copy/clear chip stops short of the cell's own edge
 * instead of sitting on it — measured at 0px of gap before it was added.
 */
export const ATTRIBUTE_CELL = `group/cell flex min-w-0 items-center gap-1 ${ATTRIBUTE_RADIUS} pr-1 transition-colors`;

/**
 * **The fade a reserved copy/clear affordance appears with** (VALE-493).
 *
 * The affordances hold their width at rest — see the reveal in
 * {@link AttributeValueCell} — so hover changes only their paint, and a paint
 * change that lands in one frame reads as a pop. 150ms is deliberately shorter
 * than any duration in {@link ATTRIBUTE_MOTION}: those move a row and want to be
 * followed, this one only stops an icon from snapping into place while the
 * pointer is still travelling across the row.
 *
 * `motion-safe:` because a reader who has asked for less motion is asking about
 * movement, and the honest answer for a pure opacity change is to show it at
 * once rather than animate it anyway.
 *
 * The property list spells out the colours as well as the opacity on purpose:
 * this lands on a button that already carries `transition-colors`, and a second
 * `transition-property` on the same element replaces the first rather than
 * adding to it — so naming opacity alone would have bought the fade by making
 * the affordance's own hover chip snap. Under reduced motion the bare
 * `transition-colors` is left in force and only the fade drops out, which is the
 * distinction that preference is actually about.
 */
export const ATTRIBUTE_AFFORDANCE_FADE =
  'motion-safe:transition-[color,background-color,opacity] motion-safe:duration-150 motion-safe:ease-out';

/**
 * What a **hugging** {@link ATTRIBUTE_CELL} adds while its editor is open — the
 * room back.
 *
 * An `<input>` sized to its own intrinsic width is ~170px in every browser, so a
 * cell that kept hugging would open a text editor a third the width of the value
 * it replaced, and the caret would sit somewhere other than where the value was.
 * A hug is about the *hit area*; typing needs the room.
 *
 * **Only chrome needs it now** (VALE-349): a grid row's cell is the value column
 * at rest as well, so there is nothing for opening to take back. It stays a
 * token of its own rather than a `flex-1` typed into the cell component — this
 * is the same geometry decision {@link ATTRIBUTE_VALUE_SLOT} makes for the
 * held-back path, and two spellings of it is how the two drift.
 */
export const ATTRIBUTE_CELL_EDITING = 'flex-1';

/**
 * **What a cell whose last commit was refused wears** (VALE-349).
 *
 * The same anatomy as {@link ATTRIBUTE_EDITOR_CHROME} — an inset ring and a
 * tinted fill, drawn on the **cell** so it encloses the value and its
 * affordances together — in the critical tone, so a refusal reads as a state
 * the row is *in* rather than as a sentence that happens to be near it. The row
 * does not change shape: a ring is box-shadow and occupies no layout, which is
 * the whole reason the open-editor chrome is one too.
 *
 * It is applied **after** the editor chrome, so a field that is both open and
 * refused — the common case, since a refusal keeps the field open — shows the
 * refusal. `focus-within:` is restated in the critical tone for the same
 * reason: the editor chrome's own focus ring would otherwise win back the
 * colour the moment the reader put the caret in the field they are being asked
 * to fix.
 */
export const ATTRIBUTE_CELL_FAILED = `${ATTRIBUTE_RADIUS} bg-error-subtle ring-1 ring-error/20 ring-inset focus-within:ring-error/40`;

/**
 * **The refusal's own line, under the value it belongs to** (VALE-349).
 *
 * It used to be one line above the whole grid — an undifferentiated sentence
 * that named its field in prose because it had no other way to point at it. On
 * the row, the label column already points; see {@link AttributeGrid}, which
 * keeps the naming half of the sentence as screen-reader text so the copy the
 * commit path produces is unchanged.
 *
 * `text-12px` is the label's own size, not the value's: this is chrome about
 * the record rather than content of it, the same argument
 * {@link ATTRIBUTE_MUTED_INK} makes for the placeholder.
 */
export const ATTRIBUTE_ROW_ERROR = `${ATTRIBUTE_LABEL_TEXT} text-error-subtle-foreground`;

/**
 * **A standing advisory on a row, in amber** (VALE-1008) — the same slot and the
 * same type as {@link ATTRIBUTE_ROW_ERROR}, one tone quieter.
 *
 * The two say different things and the colour is the whole distinction. A
 * refusal is **red** and is about *the reader's last gesture*: something they
 * just did was rejected, and the value in front of them did not save. A note is
 * **amber** and is about *the record*: nothing has failed and nothing is
 * pending, but this figure needs their attention. The share drawers' case is a
 * holding Vale cannot price automatically, where the row is working exactly as
 * intended and the solicitor simply has to enter the value by hand.
 *
 * Rendering it red would say a save failed when none was attempted, which on a
 * money row in a legal record is the more expensive of the two mistakes.
 */
export const ATTRIBUTE_ROW_NOTE = `${ATTRIBUTE_LABEL_TEXT} text-warning-subtle-foreground`;

/**
 * **How long a save may travel before the row says so** (VALE-349).
 *
 * Commit-on-blur fires and forgets, so a slow round trip showed nothing at all
 * and a reader could not tell a save still travelling from one that had already
 * landed. A spinner on every commit would be worse: the common path settles in
 * well under this, and a flicker on every field a reader leaves is noise that
 * teaches them to ignore it.
 *
 * **It is one threshold doing two jobs, deliberately.** It gates the indicator,
 * and it gates the leave-guard — closing a drawer over a save still in flight
 * prompts exactly when the reader can *see* the save is unfinished. That is what
 * lets VALE-60's close-commits behaviour stand: the commit a close gesture
 * itself fires is 0ms old, so it goes on the way it always has, silently.
 */
export const ATTRIBUTE_SAVE_INDICATOR_DELAY_MS = 500;

// =============================================================================
// The open editor
// =============================================================================

/**
 * What an open editor's **container** wears, in the refined treatment.
 *
 * Three corrections live in this one string, each from a measurement:
 *
 * · **A ring, not a border.** A border adds a pixel to the box, so opening a
 *   field nudged its value 1px right and 1px down. A ring is drawn with
 *   box-shadow and occupies no layout, so the value does not move at all.
 * · **`rounded-md`, the hover's own radius.** What read as a heavy black
 *   border was `focus-visible:ring-2` in near-black drawn *outside* the box —
 *   which is also why the radius looked wrong when the input's own 10px
 *   already matched the highlight's.
 * · **`slate-50`, the hover's own fill.** White made opening a field a jump to
 *   a *lighter* surface than the hover that preceded it, so the row appeared
 *   to flash on click.
 *
 * It belongs to the **cell**, not the input, so the border encloses the value
 * and its copy/clear buttons together — exactly as the hover tint does.
 *
 * Published vocabulary; no production call site until [ATTR-5] — see
 * {@link ATTRIBUTE_CELL}.
 */
export const ATTRIBUTE_EDITOR_CHROME = `${ATTRIBUTE_RADIUS} bg-surface-subtle ring-1 ring-border ring-inset focus-within:ring-border-strong`;

/**
 * What the control *inside* that container wears: nothing.
 *
 * The cell draws the border, the fill and the focus state, so a second border
 * inside the first is the double outline this exists to prevent.
 *
 * Published vocabulary; no production call site until [ATTR-5] — see
 * {@link ATTRIBUTE_CELL}.
 */
export const ATTRIBUTE_EDITOR_BARE =
  'border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 focus-visible:border-0';

/**
 * **A row-level affordance button — copy, clear** (VALE-863, promoted).
 *
 * Lived as a local `affordance` const inside `attribute-value-cell.tsx` while
 * that file was the only thing that drew one. The identity-variant rows draw
 * their own now (they are `render` rows, so the cell's affordances are not
 * theirs to inherit), and two copies of a 5-class string is the drift this
 * module exists to prevent.
 */
export const ATTRIBUTE_AFFORDANCE =
  'flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';

/**
 * **How an affordance reveals itself** — hidden until the row is hovered or
 * holds focus, and non-interactive until then so it cannot be clicked while
 * invisible.
 *
 * Requires a `group/cell` ancestor, which is what {@link ATTRIBUTE_CELL}
 * establishes for a normal row; a `render` row has to establish its own.
 */
export const ATTRIBUTE_AFFORDANCE_REVEALED = cnJoin(
  'opacity-0 group-focus-within/cell:opacity-100 group-hover/cell:opacity-100',
  'pointer-events-none group-focus-within/cell:pointer-events-auto group-hover/cell:pointer-events-auto'
);

/**
 * **How far a popover keeps off the viewport edge.**
 *
 * Radix flips and shifts a popover to stay on screen, but by default it will
 * sit flush against the edge, which reads as clipped whether or not anything is
 * actually cut off. A margin is the difference between "it fits" and "it looks
 * like it fits".
 */
export const ATTRIBUTE_POPOVER_COLLISION_PADDING = 12;

// =============================================================================
// The composite value — a field that is several parts
// =============================================================================

/**
 * **A nested run of rows, and the rule that says what the indent is for**
 * (VALE-997, promoted).
 *
 * For rows that belong to something above them — a disclosure's contents, a
 * confirmation's dependent fields — where the reader has to be able to tell at a
 * glance which rows are inside and which are not. A bare `pl-*` cannot do that
 * job: it lines the rows up with nothing and reads as a broken column rather
 * than as a nesting, which is the defect `ValueConfirmation`'s own note records
 * losing the form variant's `pl-6` over.
 *
 * Lived inline in `ValueConfirmation.tsx` while that was the only nested run in
 * the product. `SharePriceBlock`'s "Adjust values manually" panel is the second,
 * and two copies of the same five classes is the drift this module exists to
 * prevent.
 *
 * **Where it goes depends on whether the rows move.** `ValueConfirmation` puts
 * it on each row, because a row of that set is lifted out of flow when it
 * departs and a rule on the wrapper would hang beside nothing for the length of
 * the exit. A disclosure whose contents appear and leave as one block has no
 * such moment, so the wrapper is the right element there — and a wrapper is also
 * what can carry the `@container` those rows need.
 */
export const ATTRIBUTE_NESTED_RULE = 'ml-1 border-l border-border pl-2';

/**
 * **The width every attribute popover takes** (VALE-997), named apart from the
 * panel so a popover that supplies its own padding can still be the same width
 * as the ones that do not.
 *
 * The picker popovers are the case: they hold a `Command` list which brings its
 * own padding, so they take {@link ATTRIBUTE_POPOVER_PANEL}'s width with `p-0`
 * rather than the panel wholesale. They used to size themselves from
 * `--radix-popover-trigger-width` instead — correct when LF-6314 wrote it,
 * because the trigger was then a full-width control. VALE-997 made the resting
 * row a value that hugs its own text, so that variable stopped meaning "the
 * width of the field" and started meaning "the width of whichever institution
 * happens to be selected": `Computershare` rendered a 148px popover in which
 * the search box, the "type at least 2 characters" hint and "Add a new share
 * registry" all wrapped into a column.
 *
 * Written as its own class rather than derived from the panel's string:
 * Tailwind scans for literal class names, so a computed one is never emitted.
 */
export const ATTRIBUTE_POPOVER_W = 'w-80';

/**
 * **The width for a picker whose rows have to identify themselves** (VALE-1423) —
 * 360px, against {@link ATTRIBUTE_POPOVER_W}'s 272px.
 *
 * A second token rather than a wider `ATTRIBUTE_POPOVER_W`, because that one is shared
 * by eight popovers — the address, name, bank-account and market-figure editors, and
 * the HIN and SRN share pickers — none of which asked for more room. Widening the
 * shared token to serve one caller is how a token stops meaning what its name says.
 *
 * 272px is what made the institution picker unreadable: a 37-character facility name
 * truncates, and twenty-one facilities of one provider differ only in the part that
 * gets cut. 360px is enough for the name plus a second line saying which place it is,
 * and still narrower than the inventory drawer it opens over.
 *
 * Written as a literal arbitrary value: Vale's `--spacing` is 0.2125rem, so no step on
 * the scale lands on 360px (`w-96` is 326px), and Tailwind only emits class names it
 * can find as literals — a computed one is never generated.
 */
export const ATTRIBUTE_POPOVER_W_WIDE = 'w-[22.5rem]';

/**
 * **A composite panel never grows past the space Radix has for it** (VALE-1710).
 *
 * These panels had no height bound at all, which was survivable while
 * overflowing the fold only cost you the sight of a checkbox and an
 * explanatory paragraph. It stopped being survivable the moment the panel
 * carried the control that finishes the edit: measured on the `Other addresses`
 * variant at a 1280x720 window, the panel ran to 434px from a top of 338 and
 * put **Done** 42px below the fold, on a panel that could not scroll. The
 * reader could see every part and reach none of the finish.
 *
 * Worth knowing when checking this: **Playwright's `isVisible()` reports `true`
 * for that button.** It asks whether the element has a box and is not
 * `visibility: hidden`, not whether it is inside the viewport — so the gate this
 * closes is one an automated check can report green on while a person cannot
 * click the thing.
 *
 * `--radix-popover-content-available-height` is Radix's own measurement of the
 * room between the trigger and the collision boundary, so the panel yields
 * exactly as much as it has to and no more. Pair with
 * {@link ATTRIBUTE_POPOVER_CONFIRM_ROW}, which sticks to the bottom of the
 * scrolled area so the control keeps the one corner it has on every other
 * panel.
 *
 * Not on {@link ATTRIBUTE_POPOVER_W}: the picker panels take that with `p-0`,
 * and their `Command` list already carries its own `max-h` and scroller. A
 * second bound outside it would be two answers to one question.
 */
export const ATTRIBUTE_POPOVER_SCROLL =
  'max-h-[var(--radix-popover-content-available-height)] overflow-y-auto';

/**
 * **The panel a multi-part value is edited in** (VALE-863).
 *
 * A handful of fields on this treatment are not one value but several — an
 * address is five parts, a name is four — and §1.9's rule is that editor weight
 * scales with the shape of the value, so each opens a popover rather than
 * flattening itself into one input that has to be parsed back apart.
 *
 * That much was already settled. What was **not** shared is the panel those
 * parts sit in, and the two editors had drifted apart exactly as far as you
 * would expect from two authors:
 *
 * | | `attribute-name-editor` (VALE-129) | `attribute-address-editor` (LF-5988) |
 * | --- | --- | --- |
 * | Part label | {@link ATTRIBUTE_LABEL_TEXT} | hand-typed `text-12px` |
 * | Part input | {@link ATTRIBUTE_VALUE_TEXT} | hand-typed `text-13px` |
 * | Panel | `w-72 space-y-2 p-3` | `w-[min(28rem,…)] p-3`, gaps from `AddressSearch` |
 *
 * The name editor's header says it was *"built from the address editor
 * directly"*, and the values agree — but only because they were copied, which
 * is the second definition the root `CLAUDE.md` forbids. The address side then
 * moved (its `unified` arm delegates its parts to `AddressSearch`, whose own
 * `space-y-3` / `space-y-4` / `gap-4` are page-form spacing) and the two stopped
 * matching. Tom's 27 Aug review saw the result from the outside: the address
 * popover *"reads looser and taller than the popovers it sits alongside"*.
 *
 * So the panel is named once here, and both editors — and the next composite
 * editor after them, which is the point — read it rather than restating it.
 *
 * **Two tiers, deliberately named apart.** `ATTRIBUTE_POPOVER_*` is the *panel*:
 * a width and a padding, meaningful only to something that actually opens a
 * popover. `ATTRIBUTE_COMPOSITE_*` below is the *parts*, and those hold wherever
 * a multi-part value is edited — the identity-variant editors that VALE-863 also
 * brought onto this treatment render inline, under the record rather than in a
 * popover, and need the parts without the panel. Naming the parts `POPOVER_*`
 * would have made every one of those call sites a name that lies about its own
 * host.
 *
 * **The width is `w-80`, one step up from the name editor's `w-72`**
 * (VALE-863, second pass). At this theme's 3.4px `--spacing` those are 272px
 * and 245px, not the 320/288 a 4px scale would give. 245px fitted the name
 * editor's single-column parts but not the identity-variant editors'
 * two-across rows, whose labels, *"How this name is used"*, *"Basis for
 * reconciling"*, wrapped to two lines at ~120px a column. 272px clears them,
 * and one width for every composite popover is the point; the wide variant
 * below stays reserved for the address, which carries a lookup above its
 * parts. VALE-1505 found three popovers that had not adopted it (`multiSelect`
 * and `principalAndMembers` at `w-72`, `valueWithConfirmation` restating
 * `w-80`) and bound them.
 *
 * **The padding is `p-2.5`, not the name editor's `p-3` — a deliberate 1.7px
 * tightening on both editors, not a preserved value.** The review's ask was
 * that the compact shape become *the default* for composite editors rather than
 * the address popover alone being pulled back into line, so the shared panel
 * takes the tighter figure. Everything else about the name editor's geometry is
 * preserved exactly; see {@link ATTRIBUTE_COMPOSITE_PARTS}.
 *
 * **`pb-0` is deliberate, not an omission** (VALE-1728). The bottom inset of
 * these panels lives on {@link ATTRIBUTE_POPOVER_CONFIRM_ROW}, which every one
 * of them renders as its last child. It has to: the panel is its own scrollport
 * ({@link ATTRIBUTE_POPOVER_SCROLL}), so a bottom padding here sits *inside*
 * the scrolled area, and the confirm row would either stop short of the
 * popover's inner edge and let content scroll through the strip beneath it, or
 * reach that edge and have the panel's padding added under the control on top
 * of its own. VALE-1710 shipped the second, which put 17px under the control
 * against 10.2px above it. With the inset stated once, in the row, the control
 * sits the same distance from the bottom edge as from the content above it, and
 * the padded and padding-free panels land on the same pixels.
 *
 * `frontend/components/attributes/__tests__/attribute-popover-confirm.test.tsx`
 * is what keeps that true: a panel taking this token and rendering no confirm
 * row fails there rather than silently losing its bottom inset.
 */
export const ATTRIBUTE_POPOVER_PANEL = `${ATTRIBUTE_POPOVER_W} px-2.5 pt-2.5 pb-0 ${ATTRIBUTE_POPOVER_SCROLL}`;

/**
 * **The wide variant of that panel**, for a composite value whose parts pair
 * two across and carry a lookup above them.
 *
 * Only the address needs it: a search box plus five parts in two columns has
 * nowhere to go at {@link ATTRIBUTE_POPOVER_PANEL}'s 272px. Capped against the
 * viewport because 448px is wider than a phone, and Radix clamps a popover's
 * *position* but cannot shrink a fixed width — so the width has to yield itself.
 *
 * The padding is deliberately the same as the narrow panel's. Only the width
 * differs; a wider popover is not a looser one.
 *
 * **`pb-0` is deliberate, not an omission** (VALE-1728). The bottom inset of
 * these panels lives on {@link ATTRIBUTE_POPOVER_CONFIRM_ROW}, which every one
 * of them renders as its last child. It has to: the panel is its own scrollport
 * ({@link ATTRIBUTE_POPOVER_SCROLL}), so a bottom padding here sits *inside*
 * the scrolled area, and the confirm row would either stop short of the
 * popover's inner edge and let content scroll through the strip beneath it, or
 * reach that edge and have the panel's padding added under the control on top
 * of its own. VALE-1710 shipped the second, which put 17px under the control
 * against 10.2px above it. With the inset stated once, in the row, the control
 * sits the same distance from the bottom edge as from the content above it, and
 * the padded and padding-free panels land on the same pixels.
 *
 * `frontend/components/attributes/__tests__/attribute-popover-confirm.test.tsx`
 * is what keeps that true: a panel taking this token and rendering no confirm
 * row fails there rather than silently losing its bottom inset.
 */
export const ATTRIBUTE_POPOVER_PANEL_WIDE = `w-[min(28rem,calc(100vw-2rem))] px-2.5 pt-2.5 pb-0 ${ATTRIBUTE_POPOVER_SCROLL}`;

/**
 * **The word on the control that finishes an edit** (VALE-1710).
 *
 * One label on every composite popover, named here rather than typed into each
 * panel, for the reason §2 gives about the treatment generally: a word
 * hand-copied into eight panels is eight places for it to drift, and a reader
 * who meets *Done* on an address and *Save* on a bank account learns that the
 * two panels are different kinds of thing when they are not.
 *
 * **Why *Done* and not *Save*.** Every gesture in these panels already commits —
 * dismissing does, `Enter` does — so this control is not a distinct save step
 * that the other gestures skip. It is the visible way to finish, which is
 * exactly what was missing: the panel taught no way out of itself, and the
 * reader's worry (the ticket's own words) was that clicking away would *discard*
 * what they had typed. *Save* would answer that worry more directly and then
 * teach something false, because it frames the panel as a form with one saving
 * exit and therefore frames dismissing — the commonest gesture on this surface —
 * as the unsaved one. *Done* claims only what is true of all three paths.
 *
 * *OK* and *Apply* were the other two considered; the first reads as
 * acknowledging a dialog rather than finishing an edit, and the second is
 * software jargon in front of a non-technical reader.
 */
export const ATTRIBUTE_POPOVER_CONFIRM_LABEL = 'Done';

/**
 * **The word on the control that abandons an edit** (VALE-2080).
 *
 * It sits beside {@link ATTRIBUTE_POPOVER_CONFIRM_LABEL} and does what `Escape`
 * already did, visibly: the field goes back to what it held when the popover
 * opened, and nothing is saved. Named here for the same reason *Done* is, so
 * nine panels cannot drift to nine words.
 *
 * **Clicking outside still saves.** VALE-1710 left this control out because a
 * Cancel/Done pair reads like a dialog's, where clicking away means cancel.
 * VALE-2080 kept dismiss-commits anyway (the commonest gesture on this surface,
 * and the one VALE-1617's race fix exists to make reliable) and accepted that
 * trade: a reader who changes their mind now has a way out they can see, and
 * the pair's exit is still the familiar word rather than a second invented one.
 */
export const ATTRIBUTE_POPOVER_CANCEL_LABEL = 'Cancel';

/**
 * **The space between Cancel and Done** in the confirm strip (VALE-2080).
 * `gap-2` is 6.8px at this theme's 3.4px `--spacing`, the same step the parts
 * above the strip sit apart by ({@link ATTRIBUTE_COMPOSITE_PARTS}).
 */
export const ATTRIBUTE_POPOVER_CONFIRM_PAIR_GAP = 'gap-2';

/**
 * **The transparent gutter between the panel's content and the confirm row's
 * background** (VALE-1728).
 *
 * The row's background is opaque and, being `sticky`, it paints above the
 * fields above it — so wherever that background begins, it erases anything the
 * last field draws *outside* its own box. A focused field draws exactly that: a
 * 2px `box-shadow` ring with no spread inside the border box. With the row's
 * background starting flush at the field's bottom edge, focusing the last field
 * in a composite popover cut the bottom off its ring, which reads as the field's
 * outline being broken — the sign-off report this ticket was reopened on.
 *
 * So the space above the control is in two parts: this gutter, which is margin
 * and therefore transparent, and {@link ATTRIBUTE_POPOVER_CONFIRM_INSET}'s top
 * padding, which is background. `1` is 3.4px at this theme's `--spacing`,
 * comfortably clear of the 2px ring, and the padding is reduced by the same
 * amount so the total space above the control is unchanged.
 *
 * **Two spellings, one figure, because the two row shapes start from different
 * places.** The padded panel is a `gap-2` flex column that already contributes
 * 6.8px between children, so the gutter is written as a *partial cancellation*
 * of it (`-mt-1` leaves 3.4px of the 6.8px standing). The `p-0` picker
 * contributes no gap, so it adds the same 3.4px outright. Cancelling the gap in
 * full — which is what VALE-1710 did — is what put the background flush against
 * the field.
 */
export const ATTRIBUTE_POPOVER_CONFIRM_GUTTER_PADDED = '-mt-1';

/** The same gutter on a panel with no flex gap to cancel. See the padded one. */
export const ATTRIBUTE_POPOVER_CONFIRM_GUTTER_INSET = 'mt-1';

/**
 * **The one inset the confirm control sits at, on every side of its strip**
 * (VALE-1710, corrected under VALE-1728).
 *
 * Named, and the ONLY thing that sets those distances, because the gap above
 * the control was previously the sum of two numbers — the panel's own `gap-2`
 * between flex children, plus the row's own top pad — and a distance that is
 * the sum of two numbers is a distance nobody can keep equal. The panels that
 * supply no flex gap (the `p-0` picker) silently got the second number alone,
 * which is a different spacing for the same relationship.
 *
 * **The space above the control equals the space below it**: `pb-2.5` below,
 * and above it the gutter (`1`) plus this token's `pt-1.5` — 1 + 1.5 = 2.5, the
 * same step. VALE-1710 shipped `pt-3` above (10.2px) against `pb-2.5` below
 * (8.5px), and the control read as sitting low in its own strip rather than
 * centred in it. The split into gutter and padding is about *where the
 * background begins*, not about the distance, which is why the sum is what the
 * spec asserts — see {@link ATTRIBUTE_POPOVER_CONFIRM_GUTTER_PADDED}.
 *
 * **The figure is {@link ATTRIBUTE_POPOVER_PANEL}'s own `p-2.5` restated on
 * each axis**, not a second opinion: the strip is inset from the popover's
 * edges by exactly what holds the parts above it off those same edges, so the
 * control sits the same distance from the bottom as the fields sit from the
 * sides. 8.5px at this theme's 3.4px `--spacing`.
 *
 * Written as literal class names: Tailwind scans for literals, so a computed
 * one is never emitted.
 */
export const ATTRIBUTE_POPOVER_CONFIRM_INSET = 'px-2.5 pt-1.5 pb-2.5';

/**
 * **The row the confirm control sits in**, at the foot of a composite popover
 * (VALE-1710).
 *
 * Last child, right-aligned, in every panel — the placement is the part that has
 * to be identical, since a reader who has learned where the control is on one
 * panel should not have to look for it on the next.
 *
 * **Every distance in the strip is {@link ATTRIBUTE_POPOVER_CONFIRM_INSET} plus
 * {@link ATTRIBUTE_POPOVER_CONFIRM_GUTTER_PADDED} and nothing else**, so the
 * spacing stops being the sum of a panel's choice and a row's choice and becomes
 * the one pair of tokens every panel reads.
 *
 * It is a row rather than a bare button so the alignment lives here and not on
 * the button, where it would have to be a class — and `className` is deleted
 * from `ButtonProps`.
 *
 * **`sticky bottom-0` is what keeps "the same corner" true on a panel that
 * scrolls** ({@link ATTRIBUTE_POPOVER_SCROLL}). The `Other addresses` variant is
 * the tall one — a lookup, five parts, a type, a period, a checkbox and a
 * paragraph — and letting the control scroll away with them would answer the
 * height problem by making the reader hunt for the finish, which is the thing
 * this whole change exists to stop.
 *
 * `-mx-2.5` with `px-2.5` back on is not decoration: it pulls the row out to the
 * panel's side edges so `bg-popover` masks the parts sliding underneath, then
 * re-pays the padding it just cancelled. Without it the row is translucent at
 * the seams and the last part shows through.
 *
 * **The bottom needs no such offset, because the panel carries no bottom
 * padding** ({@link ATTRIBUTE_POPOVER_PANEL}'s `pb-0`). The row is the last
 * child, so its box already ends at the popover's inner edge, stuck or not —
 * which is what makes the strip measure the same in both states. VALE-1710
 * instead left the panel's `p-2.5` in place and pulled the sticky stop down past
 * it (`-bottom-2.5`), so the control had 17px beneath it when the panel did not
 * scroll and 8.5px when it did.
 */
export const ATTRIBUTE_POPOVER_CONFIRM_ROW = `sticky bottom-0 -mx-2.5 ${ATTRIBUTE_POPOVER_CONFIRM_GUTTER_PADDED} flex justify-end ${ATTRIBUTE_POPOVER_CONFIRM_PAIR_GAP} bg-popover ${ATTRIBUTE_POPOVER_CONFIRM_INSET}`;

/**
 * **The same row, for the panel that supplies no padding of its own.**
 *
 * The `multiSelect` picker takes {@link ATTRIBUTE_POPOVER_W} with `p-0`, because
 * the `Command` list inside it brings its own padding (see that token). So the
 * footer has to carry the panel padding the panel is not carrying, or the
 * control would sit flush against the popover's edge while the identical control
 * one row up in the address panel sits 8.5px inside it.
 *
 * **It is the shared {@link ATTRIBUTE_POPOVER_CONFIRM_INSET} and the same
 * gutter**, which is what makes the two shapes land on the same pixels: this
 * panel has no padding to cancel and no flex gap to cancel, so it carries
 * neither the side offset nor the sticky stop that pay those back on the padded
 * row, and states the gutter outright rather than as a partial cancellation.
 */
export const ATTRIBUTE_POPOVER_CONFIRM_ROW_INSET = `flex justify-end ${ATTRIBUTE_POPOVER_CONFIRM_PAIR_GAP} bg-popover ${ATTRIBUTE_POPOVER_CONFIRM_GUTTER_INSET} ${ATTRIBUTE_POPOVER_CONFIRM_INSET}`;

/**
 * **The gap between one part and the next**, inside either panel.
 *
 * `gap-2` on a flex column at this theme's 3.4px `--spacing` is **6.8px** —
 * tight enough that four stacked parts read as one value rather than four
 * fields, which is the whole reason they share a popover. The name editor's
 * `space-y-2` computed the same and is preserved exactly; the address side is
 * what moves, from `AddressSearch`'s `space-y-4` (13.6px).
 */
export const ATTRIBUTE_COMPOSITE_PARTS = 'flex flex-col gap-2';

/**
 * **One part: its label above its control.**
 *
 * `gap-1` — 3.4px — binds the label to the control it names more tightly than
 * the parts are bound to each other, so the pairing is legible without a box
 * around it. The name editor's `space-y-1` computed the same figure; this is
 * that value, named.
 *
 * **`space-y-0` is load-bearing, not redundant.** This token is also handed to
 * `ui/form-fields`' `TextField` (see its `labelClassName` / `controlClassName`
 * note), whose wrapper carries a page-form `space-y-2` that stays additive —
 * a shipped test asserts as much. `space-y-*` is margin on the children, which
 * `gap` does not conflict with, so without the reset a compact part would carry
 * both and be *looser* than the field it replaced. Merged through `cn`,
 * `space-y-0` wins and the gap alone spaces the part. Inside this package it is
 * a no-op: a flex column of two children has no sibling margin to clear.
 */
export const ATTRIBUTE_COMPOSITE_PART = 'flex flex-col gap-1 space-y-0';

/**
 * **One part whose control is too short to earn a line of its own** — the label
 * and the control side by side, label first, control at the far end (VALE-1247).
 *
 * {@link ATTRIBUTE_COMPOSITE_PART} stacks a label above its control, which is
 * right for the parts that are a full-width `Input` or `Textarea`: the control
 * needs the width, so the label has nowhere to go but above it. A `Yes` / `No`
 * segmented pair is about 70px, so stacking it spent a whole line of the
 * popover on two characters and left the rest of that line empty, between two
 * inputs that fill theirs. Beside its label the same pair costs no extra height
 * and reads as the short flag it is.
 *
 * **The control sits next to its label, not at the far end of the panel.**
 * `justify-between` was the first attempt and it read as two unrelated things
 * at opposite edges: the panel is as wide as the inputs above need, so pushing
 * a 70px control to the right margin put most of the popover's width between a
 * label and the answer it names. A plain `gap-3` keeps the pair reading as one
 * part, which is the whole reason it is on one line.
 *
 * `items-center` is what actually pairs them: the label is 12px text and the
 * control is a 20px track, so aligning to the start would leave the label
 * riding high against a control it names.
 */
export const ATTRIBUTE_COMPOSITE_PART_INLINE =
  'flex flex-row items-center gap-3 space-y-0';

/**
 * **A part's label.** The same size as a row label on the grid behind the
 * popover ({@link ATTRIBUTE_LABEL_TEXT}), because it is the same kind of thing.
 */
export const ATTRIBUTE_COMPOSITE_PART_LABEL = ATTRIBUTE_LABEL_TEXT;

/**
 * **A part's control** — an `Input`, or a `Select`'s trigger.
 *
 * {@link ATTRIBUTE_VALUE_TEXT} to match the value it is editing, and `py-0.5`
 * to bring the control down from `ui/input`'s page-form height: at 13px text
 * the default padding leaves a control taller than the row it opened from, and
 * five of them stacked is what made the address panel tall. `h-auto` is needed
 * on a `SelectTrigger`, which sets its own height.
 */
export const ATTRIBUTE_COMPOSITE_PART_CONTROL = `${ATTRIBUTE_VALUE_TEXT} h-auto py-0.5`;

// =============================================================================
// The segmented boolean
// =============================================================================

/**
 * **The live `Yes` / `No` segmented control's track** (LF-6186 round 2/3/4,
 * promoted to production by LF-6350 / [ATTR-8] — story 7a's `booleanControl`).
 *
 * A track-and-pill segmented control, the same anatomy the grouped-form
 * prototype settled on for Individual / Organisation, so the two binary choices
 * on this treatment look like each other rather than like two inventions.
 *
 * The three hexes were the prototype's own line/ink hierarchy, carried across
 * verbatim for the same reason {@link ATTRIBUTE_EDITOR_CHROME}'s `#e4e4ea` and
 * `#c9c9d4` were: a `slate-*` approximation is a *different* colour, and the
 * whole point of promoting a measured prototype is that the measurements survive.
 *
 * **VALE-531 (DARK-7b) resolved all five onto neutral tokens, and the objection
 * above was right about `slate-*` and wrong about the tokens.** Measured, the
 * five land Δ4, Δ8, Δ9, Δ11 and Δ12 (max per-channel sRGB) from `border`,
 * `muted-foreground`, `foreground`, `border` and `border-strong` — the same order
 * as the neutral cool-cast correction DARK-4 measured and this initiative
 * accepted across several thousand call sites, and far closer than the `slate-*`
 * steps the argument was made against. The measurements do survive. What they buy
 * in exchange is the thing a raw hex structurally cannot do: follow the theme. A
 * hardcoded `#ebebf0` track is a light-grey slab on a dark ground for ever.
 */
/**
 * The track — **a two-column grid, not a flex row**, and that is what makes the
 * sliding pill's 50% geometry true (see {@link ATTRIBUTE_SEGMENTED_PILL}).
 *
 * `grid-cols-2` gives two `1fr` columns, which intrinsic sizing resolves to *the
 * wider label, twice* — so the halves are exactly equal and the track still
 * shrinks to fit its content. `flex-1` on the segments was tried first and is
 * the trap: `flex: 1 1 0%` zeroes the basis, so `w-fit` sized the track from a
 * zero-width contribution and `Not done` wrapped onto two lines.
 *
 * `grid` overrides the `flex` on `ToggleGroup`'s own base class through
 * tailwind-merge, which resolves the `display` group in favour of the passed
 * `className`. Interaction, keyboard and the accessibility tree are untouched —
 * this changes how the two children are laid out, nothing about what they are.
 */
export const ATTRIBUTE_SEGMENTED_TRACK =
  'relative grid w-fit grid-cols-2 justify-start gap-0 rounded-md bg-border p-0.5';

/**
 * One segment of that track: transparent, because the fill is drawn by the
 * sliding pill behind it ({@link ATTRIBUTE_SEGMENTED_PILL}).
 *
 * `rounded-md` matches the track and the row's hover highlight — it was an
 * arbitrary `rounded-[4px]` inherited from the larger control, and at this size a
 * 4px pill inside a 10px track read as the wrong shape rather than a nested one.
 * Kept for the focus ring, which still traces the segment.
 */
export const ATTRIBUTE_SEGMENTED_SEGMENT = `${ATTRIBUTE_LABEL_TEXT} relative z-10 h-5 rounded-md bg-transparent px-2 text-center whitespace-nowrap text-muted-foreground transition-colors data-[state=on]:bg-transparent data-[state=on]:text-foreground`;

/**
 * The white pill that marks the active segment — **one absolutely-positioned
 * element that translates**, not a background on whichever segment is on.
 *
 * Moving `bg-white` between two segments is instantaneous, so a toggle read as a
 * flicker rather than as a movement — and on a control whose only job is to say
 * which of two states you are in, the transition *is* the feedback. It is half
 * the track's inner width and moves by exactly its own width.
 *
 * **That geometry is an agreement with the segments, not a property of the pill
 * alone**, and this comment used to claim it "holds at any label length without
 * measuring anything". It does not, and did not: a 50%-wide pill lands on the
 * active segment only while the two segments are *themselves* 50% each. With
 * content-width segments the claim silently fails the moment the two labels
 * differ in width — `Yes` / `No` are close enough to look right, and the
 * enquiry's `Done` / `Not done` is not, so the pill sat across the boundary and
 * the longer label crowded the track's edge.
 *
 * The two-column grid on {@link ATTRIBUTE_SEGMENTED_TRACK} is what makes the
 * claim true rather than lucky: both segments take exactly half, the track still
 * shrinks to fit, and nothing has to be measured. **Change one of these two
 * tokens and you have changed the other's contract** — that is the geometry
 * agreement the segmented control's own header warns a copy would break.
 *
 * `motion-safe` so the slide is skipped for anyone whose OS asks for reduced
 * motion — the pill still lands in the right place, it just gets there at once.
 */
export const ATTRIBUTE_SEGMENTED_PILL =
  'pointer-events-none absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-card shadow-sm motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out';

// =============================================================================
// Motion
// =============================================================================

/**
 * The motion vocabulary. {@link useRowMotion} is what applies it — this object
 * is passed to the hook rather than read by it, so the hook stays free of any
 * one surface's tokens. `satisfies RowMotionSpec` is the binding that keeps the
 * two in step: drop a key here and the hook's contract fails at compile time
 * rather than at the next gate flip.
 *
 * The durations are not interchangeable and the curves are not decoration —
 * each was set against a measured failure:
 *
 * · **`exitMs` is shorter than `enterMs`, and front-loaded.** At 200ms on
 *   `ease-in` a departing row was still at opacity 0.49 when the row gliding
 *   into its place arrived 5px away — two rows legible in the same spot. On
 *   this curve it reads 0.07 at the same moment and 0.00 by 150ms.
 * · **`maxTravelPx` caps how far anything is asked to move.** A vertical move
 *   is one row (~30px) and glides beautifully; a same-line column swap is
 *   141px — half the form — and animating the true distance reads as a field
 *   flying across the drawer. A long move starts 32px out instead, and the
 *   remainder is skipped in the frame the layout changes, so there is nothing
 *   to see. 32 rather than a rounder 24 so a one-row vertical move still
 *   animates in full.
 */
export const ATTRIBUTE_MOTION = {
  /** A row arriving: fade and settle the last few pixels. */
  enterMs: 240,
  /** A row leaving. Shorter than the entrance so it clears the spot first. */
  exitMs: 180,
  /** A row making way for another, or closing the gap one left. */
  glideMs: 320,
  /** How far a row lifts as it fades out, and drops in as it arrives. */
  offsetPx: 4,
  /** The furthest any row is asked to travel, whatever the real distance. */
  maxTravelPx: 32,
  /** Decelerating. Used for arrivals and glides. */
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** Front-loaded, so most of an exit's fade happens in its first third. */
  exitEase: 'cubic-bezier(0, 0, 0.2, 1)'
} as const satisfies RowMotionSpec;
